import DashboardTile from '../../../components/DashboardTile'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import Subheader from 'app/frontend/base/components/Subheader'
import { useTranslation } from 'react-i18next'
import TeamSwitcher from '../../TeamDashboard/components/TeamSwitcher'
import HeaderTiles from '../../TeamDashboard/components/HeaderTiles'
import TeamSubheader from '../../TeamDashboard/components/TeamSubheader'
import { Link } from 'react-router-dom'
import { useGuild } from 'app/frontend/base/config/context/guild'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { premiumColorForGuild } from 'app/frontend/base/helpers'
import { Gem } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import PremiumBenefitsModal from '../../TeamDashboard/components/PremiumBenefitsModal'
import { useState } from 'react'
import EntityLinksTile from './EntityLinksTile'
import { useBreakpoint } from 'app/frontend/base/hooks/useBreakpoint'
import { hasAdminAccessToAllTeams } from 'app/frontend/base/config/permissions'
import { shouldDisplayGuildHousingPage } from 'app/frontend/base/helpers/housingPageDisplay'
import { MenuItem } from 'app/frontend/base/components/EntityMenu'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { usePageInfo } from 'app/frontend/base/config/queries'
dayjs.extend(RelativeTime)

interface Props {
  uriName?: string
  uriRegion?: string
  uriRealm?: string
}

export const useGuildMenuItems = (basePath = ''): MenuItem[] => {
  const { guild, guildReference } = useGuild()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { customDomain } = useCustomDomain()
  const { t } = useTranslation()
  const guildInfo = guild || guildReference
  const showHousing =
    gameVersion === 'live' && shouldDisplayGuildHousingPage(guild)
  const canManageGuild = hasAdminAccessToAllTeams(guild, 'base')
  const pathFor = (path: string) =>
    basePath ? `${basePath}${path}` : path || '/'

  if (!guildInfo?.id) return []

  return [
    ...(customDomain?.headerConfig?.hideOverviewMenuItem
      ? []
      : ([
          {
            name: t('shared.overview'),
            path: pathFor(''),
            width: 94,
            exact: true,
          },
        ] as MenuItem[])),
    {
      name: t('shared.teams'),
      path: pathFor('/teams'),
      width: 75,
    },
    {
      name: t('shared.news'),
      path: pathFor('/news'),
      width: 69,
    },
    {
      name: t('shared.members'),
      path: pathFor('/members'),
      width: 94,
    },
    ...(showHousing
      ? [
          {
            name: t('shared.housing'),
            path: pathFor('/housing'),
            width: 88,
          },
        ]
      : []),
    ...(canManageGuild
      ? [
          {
            name: t('shared.settings'),
            path: pathFor('/settings'),
            width: 88,
          },
        ]
      : [
          {
            name: t('shared.premium'),
            path: pathFor('/premium'),
            width: 85,
          },
        ]),
  ]
}

const GuildHeader = ({ uriName, uriRegion, uriRealm }: Props) => {
  const { guild, guildReference, teamReference } = useGuild()
  const { customDomain } = useCustomDomain()
  const [premiumBenefitsOpen, setPremiumBenefitsOpen] = useState(false)
  const guildInfo = guild || guildReference
  const breakpoint = useBreakpoint()

  const uriLinkFragment = `${uriRegion}/${uriRealm}/${uriName}`
  const basePath =
    customDomain?.guild && !customDomain.team
      ? ''
      : guildInfo?.path
      ? guildInfo.path
      : `/guild/${uriLinkFragment}`
  const guildMenuItems = useGuildMenuItems(basePath)

  const guildName = guildInfo?.name || uriName?.replaceAll('-', ' ')
  const wordCount = guildName?.split(' ').length

  return (
    <>
      <header className='root-width'>
        <div className='border-b-2 -ml-4 -mr-4 sm:-ml-8 sm:-mr-8'>
          <div className='flex items-center -mt-8 ml-4 mr-4 sm:ml-8 sm:mr-8 '>
            <BannerImage
              className='hidden sm:block mr-4 ml-[-0.7rem]'
              faction={guildInfo?.faction}
              fileName={guildInfo?.bannerFilename}
              size='large'
            />

            <div className='grow mb-2'>
              <div className='flex justify-between items-end flex-wrap gap-2'>
                <div className={`flex flex-col mt-2`}>
                  <span
                    className={`text-sm text-muted-foreground capitalize h-[1.6rem] leading-6`}
                  >
                    {guildInfo?.realm?.nameWithRegion ||
                      `${uriRealm} (${uriRegion?.toUpperCase()})`}
                  </span>

                  <div className='flex items-center gap-3'>
                    <Link to={basePath || '/'}>
                      <h1 className='leading-none flex items-center gap-2 sm:mb-2 max-sm:text-xl'>
                        {breakpoint === 'xs' ? (
                          <BannerImage
                            faction={guildInfo?.faction}
                            fileName={guildInfo?.bannerFilename}
                            size='small'
                          />
                        ) : null}

                        <span
                          className={`capitalize ${
                            wordCount === 1 ? 'break-all' : ''
                          } `}
                        >
                          {guildInfo?.name || uriName?.replaceAll('-', ' ')}
                        </span>
                      </h1>
                    </Link>

                    {guild?.premiumPoints && guild?.premiumPoints > 0 ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <Gem
                            onClick={() => setPremiumBenefitsOpen(true)}
                            className={`w-4 h-4 ${
                              premiumColorForGuild(guild)?.className
                            } sm:mb-1 sm:w-5 sm:h-5`}
                          />
                        </TooltipTrigger>
                        <TooltipContent className='flex flex-col gap-0.5 items-center'>
                          <span
                            className={premiumColorForGuild(guild)?.className}
                          >
                            {premiumColorForGuild(guild)?.tooltip}
                          </span>{' '}
                          <span className='text-muted-foreground text-xs'>
                            {guild?.premiumPoints} boosts applied
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    ) : null}

                    <PremiumBenefitsModal
                      open={premiumBenefitsOpen}
                      setOpen={setPremiumBenefitsOpen}
                    />
                  </div>
                </div>

                <div className='flex gap-2'>
                  {teamReference ? (
                    <HeaderTiles />
                  ) : (
                    <>
                      <DashboardTile
                        className='hidden sm:block'
                        innerClassName='h-8'
                        title='members'
                        dataPresent={true}
                      >
                        <div>{guild?.guildMembers || '-'}</div>
                      </DashboardTile>

                      <DashboardTile
                        className='hidden md:block'
                        innerClassName='h-8'
                        title='founded'
                        dataPresent={true}
                      >
                        <div>
                          {guildInfo?.foundedAt
                            ? dayjs(guildInfo?.foundedAt).fromNow()
                            : '-'}
                        </div>
                      </DashboardTile>
                    </>
                  )}

                  <EntityLinksTile uriLinkFragment={uriLinkFragment} />
                </div>
              </div>

              <div className='h-[1.6rem] max-sm:mt-1'>
                <TeamSwitcher />
              </div>
            </div>
          </div>
        </div>
      </header>

      {teamReference ? (
        <TeamSubheader />
      ) : guildInfo?.id && !(customDomain?.guild && !customDomain.team) ? (
        <Subheader menuItems={guildMenuItems} />
      ) : null}
    </>
  )
}

export default GuildHeader
